import React from 'react';
import { SchemeBadge, cardLast4 } from './savedCardUtils';
import { CARD_CONSENT_TEXT } from './cardConsent';
import { cardRailLabel, cardRailSubLabel } from './cardRail';
import './savedCards.css';

// Controlled saved-card picker for the booking checkout (RequestBookingPage
// card mode — its ONLY consumer since the wallet top-up went one-time-checkout,
// 2026-07-17). Renders one option per active saved card ("Saved card —
// Visa •••• 5940"), plus a "Use a new card" option. These are one-off payments
// the client is making right now; nothing here enrols them in auto payments. When a new card is chosen, a "Save this card for
// future payments" checkbox reveals the full consent copy; ticking it is the
// explicit save-card consent.
//
// The parent turns `value` / `saveNew` into the request body:
//   * value is a number  → { payment_method_id: value }
//   * value === 'new' && saveNew → { save_card: true, save_card_consent: true }
//   * value === 'new' && !saveNew → {} (plain one-off new card)
//
// Props:
//   cards            active cards from GET /api/cards
//   value            selected card id (number) | 'new'
//   onChange(value)
//   saveNew          bool (only meaningful when value === 'new')
//   onSaveNewChange(bool)
//   disabled
//   newCardSub       sub-label under "Use a new card". Defaults to the
//                    capability-derived copy from cardRail.js — pass a custom
//                    string only for flow-specific wording, and NEVER name a
//                    payment rail the gateway does not offer (see cardRail.js).
//   paymentsMode     optional GET /api/payments/mode payload; only its
//                    capability flags are read (apple_pay). Absent = card only.
//   allowSaveNew     when false, the "Save this card" checkbox + consent are
//                    hidden entirely (one-off card payments only — card-saving
//                    lives in its own flow). Defaults true.
export default function SavedCardPicker({
    cards = [],
    value,
    onChange,
    saveNew = false,
    onSaveNewChange,
    disabled = false,
    allowSaveNew = true,
    paymentsMode = null,
    newCardSub = null,
    // The security code lives INSIDE the selected card's row (2026-08-13)
    // — one card, one line, no repetition below. The row is a flex container
    // holding the choose-button and the field, so the input is never nested
    // inside a <button>.
    cvvValue = null,
    onCvvChange = null,
    onCvvSubmit = null,
}) {
    const newCardSubText = newCardSub || cardRailSubLabel(paymentsMode);
    const isNew = value === 'new';

    return (
        <div className="hcard-picker">
            {cards.map((card) => {
                const active = value === card.id;
                const showCvv = active && typeof onCvvChange === 'function';
                return (
                    <div key={card.id} className={`hcard-option ${active ? 'is-active' : ''}`}>
                    <button
                        type="button"
                        className="hcard-optionBtn"
                        onClick={() => onChange(card.id)}
                        disabled={disabled}
                        aria-pressed={active}
                    >
                        <span className="hcard-radio" aria-hidden="true" />
                        <span className="hcard-option-main">
                            {/* P2: was "Auto Payment — …". A stored token is a
                                stored token; calling it "Auto Payment" told
                                every client with a saved card that they were
                                enrolled in automatic charging when they were
                                not. Enrolment lives in Account only. */}
                            <span className="hcard-option-label">
                                <SchemeBadge scheme={card.scheme} />
                                <span>
                                    {String(card.nickname || '').trim() || 'Saved card'} •••• {cardLast4(card.masked_pan)}
                                </span>
                            </span>
                            {/* Card expiry is never shown to clients (2026-07-19) —
                                the label already identifies the card by scheme + last4. */}
                        </span>
                    </button>
                    {showCvv && (
                        <input
                            className="hcard-cvv"
                            type="password"
                            inputMode="numeric"
                            autoComplete="cc-csc"
                            maxLength={4}
                            placeholder="CVV"
                            aria-label="Card security code"
                            value={cvvValue || ''}
                            onChange={(e) => onCvvChange(e.target.value.replace(/\D/g, ''))}
                            onKeyDown={(e) => { if (e.key === 'Enter' && onCvvSubmit) onCvvSubmit(); }}
                            disabled={disabled}
                        />
                    )}
                    </div>
                );
            })}

            <button
                type="button"
                className={`hcard-option hcard-option--single ${isNew ? 'is-active' : ''}`}
                onClick={() => onChange('new')}
                disabled={disabled}
                aria-pressed={isNew}
            >
                <span className="hcard-radio" aria-hidden="true" />
                <span className="hcard-option-main">
                    <span className="hcard-option-label">
                        {cards.length > 0 ? 'Use a new card or Apple Pay' : cardRailLabel(paymentsMode)}
                    </span>
                    <span className="hcard-option-sub">{newCardSubText}</span>
                </span>
            </button>

            {isNew && allowSaveNew && (
                <>
                    <label className="hcard-save">
                        <input
                            type="checkbox"
                            checked={saveNew}
                            onChange={(e) => onSaveNewChange(e.target.checked)}
                            disabled={disabled}
                        />
                        <span>Save this card for future payments</span>
                    </label>
                    {saveNew && (
                        <div className="hcard-consent hcard-consent--readonly">
                            <span className="hcard-consent-text">
                                {/* CARD_CONSENT_TEXT is v3: a storage disclosure that
                                    already states, in its own words, that saving a
                                    card authorises no charge. The extra qualifier
                                    that used to sit here repeated that point and is
                                    gone — one clear sentence beats two. */}
                                <span className="hcard-consent-mandate">{CARD_CONSENT_TEXT}</span>
                            </span>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// Build the checkout body fragment for saved-card / save-card intent from the
// picker state. Kept next to the picker so both checkout hosts stay in sync.
export function cardCheckoutBody(value, saveNew) {
    if (typeof value === 'number') return { payment_method_id: value };
    if (value === 'new' && saveNew) return { save_card: true, save_card_consent: true };
    return {};
}
